import type { ApiClient } from '@carbonwebmail/api-client'
import type { BrandProfileDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { when } from '../../ui/format.js'

/**
 * What the designer has learned about this sender, and a way to forget it.
 *
 * **Shown because it is invisible influence otherwise.** The profile is given
 * to the model before every design; somebody who cannot read it cannot tell why
 * their emails keep coming out navy, and cannot correct it when the company
 * rebrands. Memory that steers output and cannot be inspected is the kind that
 * gets blamed for everything and fixed by nobody.
 *
 * Forgetting is the other half. It is one press with no confirmation, because
 * what it destroys is a paragraph that attaching the files again rewrites —
 * unlike every other delete in this product, this one is cheap to undo.
 */
export function LearnedBrand({ client }: { readonly client: ApiClient }) {
  const [brand, setBrand] = useState<BrandProfileDto['brand']>(null)
  /** Absent until asked, so the panel does not flash "nothing learned" first. */
  const [loaded, setLoaded] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const load = useCallback(async () => {
    try {
      setBrand(await client.brandProfile())
      setLoaded(true)
    } catch {
      // Not folded into "nothing learned yet": a profile that exists and could
      // not be read must not be reported as one that does not exist.
      setProblem('We could not read what has been learned. Try again in a moment.')
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  const forget = async () => {
    setProblem(undefined)
    try {
      await client.forgetBrandProfile()
      setBrand(null)
    } catch {
      setProblem('We could not forget that. Try again in a moment.')
    }
  }

  if (problem !== undefined) return <Alert>{problem}</Alert>
  if (!loaded || brand === null) return null

  return (
    <section className="mt-4 rounded-card border border-line bg-surface-2 p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-[13px] text-t1">What the designer knows about you</h3>
        <button
          className="font-medium text-[12px] text-t3 hover:text-danger"
          onClick={() => void forget()}
          type="button"
        >
          Forget this
        </button>
      </div>

      <p className="whitespace-pre-wrap text-[13px] text-t2 leading-relaxed">{brand.profile}</p>

      <p className="mt-2.5 text-[12px] text-t3">
        Learned from {brand.learnedFrom.join(', ')} · {when(brand.updatedAt)}. Attach newer files to
        replace it.
      </p>
    </section>
  )
}
